import { ModalConfirmation, ModalProps, notify, useModal, withModalConditionalRender } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../../config/queryClient'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { QuoteRelatedDraftInvoiceType } from '../../enums/quoteRelatedDraftInvoiceType'
import { useDeleteQuote } from '../../hooks/quotes/useDeleteQuote'
import { Quote } from '../../types/quotes/quote'
import { getQuoteQueryKey } from '../../utils/getQuoteQueryKey'
import { getDeleteQuoteModalContent } from './utils/getDeleteQuoteModalContent'

interface DeleteQuoteModalProps extends ModalProps {
  onClose?: () => void
  onConfirm?: () => void
  onSuccess?: () => void
  quoteDocumentType: Quote['documentType']
  quoteId: Quote['id']
  // when quote has a related draft invoice (deposit or regular), it will be deleted
  // along with quote - and the modal shows a stronger warning
  relatedDraftInvoiceType?: QuoteRelatedDraftInvoiceType
}

const DeleteQuoteModalComponent = ({
  id,
  onClose,
  onConfirm,
  onSuccess,
  quoteDocumentType,
  quoteId,
  relatedDraftInvoiceType,
}: DeleteQuoteModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(id, { onClose })

  const { cancelLabel, description, okLabel, title } = getDeleteQuoteModalContent(relatedDraftInvoiceType, t)

  const { deleteQuote, isProcessing } = useDeleteQuote({
    documentType: quoteDocumentType,
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.Quotes)
      queryClient.invalidateQueries(QueryKeys.QuotesSummary)
      queryClient.invalidateQueries(QueryKeys.ClientQuotes) // distinct key from Quotes — not covered by prefix match above
      queryClient.invalidateQueries(getQuoteQueryKey(String(quoteId)))
      // BE cascade-deletes the related draft invoice along with the quote - refresh the
      // invoices list so the deleted draft's row doesn't linger until an unrelated refetch.
      if (relatedDraftInvoiceType) {
        queryClient.invalidateQueries(QueryKeys.InvoicesZervant)
        queryClient.invalidateQueries(QueryKeys.ClientInvoicesZervant)
      }
      notify({
        id: NotificationKeys.QuoteDeleted,
        message: t('quotes.delete_modal.notification.success'),
        variant: 'success',
      })
      closeModal()
      onSuccess?.()
    },
    onError: () => {
      notify({
        id: NotificationKeys.QuoteDeleted,
        message: t('error_saving'),
        variant: 'error',
      })
    },
  })

  const handleOk = useCallback(() => {
    onConfirm?.()
    deleteQuote(quoteId)
  }, [deleteQuote, onConfirm, quoteId])

  return (
    <ModalConfirmation
      cancelLabel={cancelLabel}
      danger
      id={id}
      message={description}
      okLabel={okLabel}
      okLoading={isProcessing}
      onOk={handleOk}
      title={title}
    />
  )
}

export const DeleteQuoteModal = withModalConditionalRender(DeleteQuoteModalComponent)
